import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, ArrowRight, Anchor, Ship, Zap, Tent, Building2, Truck, Moon, CalendarClock, Fuel, Factory, GraduationCap, Tractor, Waves, Flag } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { FaqAccordion } from "../components/FaqAccordion";
import { CtaBand } from "../components/CtaBand";
import { SERVICES } from "../data/services";
import { CITIES } from "../data/cities";
import { REGIONS } from "../data/regions";
import { CITY_IMAGES } from "../data/site";

const ICONS = { anchor: Anchor, ship: Ship, zap: Zap, tent: Tent, building: Building2, truck: Truck, moon: Moon, calendar: CalendarClock, fuel: Fuel, factory: Factory, graduation: GraduationCap, tractor: Tractor, waves: Waves, flag: Flag };

export default function RegionPage() {
    const { slug } = useParams();
    const region = REGIONS.find((r) => r.slug === slug);
    if (!region) return <Navigate to="/locations" replace />;
    const cities = CITIES.filter((c) => region.citySlugs.includes(c.slug));

    return (
        <div data-testid={`region-${region.slug}-page`}>
            <Seo title={region.seoTitle} description={region.seoDescription} />
            <PageHero
                image={CITY_IMAGES[region.image % CITY_IMAGES.length]}
                imageAlt={`${region.name} security services Scotland`}
                kicker={region.kicker}
                title={region.title}
                titleAccent={region.titleAccent}
                sub={region.sub}
                testidPrefix={`region-${region.slug}`}
            />

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid={`region-${region.slug}-intro`}>
                <div className="grid lg:grid-cols-2 gap-14">
                    <Reveal>
                        <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">{region.introKicker}</p>
                        <h2 className="font-display uppercase font-extrabold text-3xl sm:text-4xl tracking-wide text-white leading-none">
                            {region.headingMain} <span className="text-[#E53E3E]">{region.headingAccent}</span>
                        </h2>
                        <p className="text-gray-300 text-sm sm:text-base leading-relaxed mt-6">{region.p1}</p>
                        <p className="text-gray-400 text-sm sm:text-base leading-relaxed mt-4">{region.p2}</p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 self-start">
                        {region.differentiators.map((d, i) => {
                            const Icon = ICONS[d.icon];
                            return (
                                <Reveal key={d.title} delay={i * 0.06} className="bg-[#0F1117] p-6 h-full">
                                    <Icon size={18} className="text-[#E53E3E] mb-3" />
                                    <h3 className="font-display uppercase font-bold text-lg tracking-wide text-white">{d.title}</h3>
                                    <p className="text-gray-500 text-sm leading-relaxed mt-2">{d.text}</p>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="bg-[#0F1117] border-y border-white/10 py-20" data-testid={`region-${region.slug}-locations`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">Regional Coverage</p>
                        <h2 className="font-display uppercase font-extrabold text-3xl sm:text-4xl tracking-wide text-white leading-none">
                            {cities.length} towns. <span className="text-[#E53E3E]">One regional command.</span>
                        </h2>
                        <p className="text-gray-400 text-sm leading-relaxed mt-4 max-w-2xl">
                            Every location below has its own dedicated page with local areas covered, services and FAQs.
                        </p>
                    </Reveal>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-10">
                        {cities.map((c) => (
                            <Link
                                key={c.slug}
                                to={`/locations/${c.slug}`}
                                data-testid={`region-${region.slug}-city-${c.slug}`}
                                className="group flex items-center gap-2.5 bg-[#0F1117] hover:bg-[#161922] transition-colors duration-300 px-5 py-4"
                            >
                                <MapPin size={14} className="text-[#E53E3E] shrink-0" />
                                <span>
                                    <span className="block text-sm text-gray-200 group-hover:text-white transition-colors">Security {c.name}</span>
                                    <span className="block font-mono-xp text-[10px] uppercase tracking-widest text-gray-600 mt-0.5">{c.region}</span>
                                </span>
                                <ArrowRight size={13} className="ml-auto text-gray-700 group-hover:text-[#E53E3E] group-hover:translate-x-1 transition-all duration-300 shrink-0" />
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid={`region-${region.slug}-services`}>
                <Reveal>
                    <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">The Arsenal // {region.name}</p>
                    <h2 className="font-display uppercase font-extrabold text-3xl sm:text-4xl tracking-wide text-white leading-none">
                        All 17 services, <span className="text-[#E53E3E]">one regional standard</span>
                    </h2>
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-10">
                    {SERVICES.map((s) => (
                        <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            data-testid={`region-${region.slug}-service-${s.slug}`}
                            className="group flex items-center justify-between bg-[#07080B] hover:bg-[#161922] transition-colors duration-300 px-5 py-4"
                        >
                            <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{s.name} {region.name}</span>
                            <ArrowRight size={13} className="text-gray-700 group-hover:text-[#E53E3E] group-hover:translate-x-1 transition-all duration-300 shrink-0" />
                        </Link>
                    ))}
                </div>
            </section>

            <section className="bg-[#0F1117] border-t border-white/10 py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display uppercase font-extrabold text-3xl sm:text-4xl tracking-wide text-white leading-none mb-10">
                            {region.name} security, <span className="text-[#E53E3E]">answered</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <FaqAccordion faqs={region.faqs} testidPrefix={`region-${region.slug}-faq`} />
                    </Reveal>
                </div>
            </section>

            <CtaBand title={region.cta} />
        </div>
    );
}
